/**
 * Serializes font family values to a valid CSS string.
 *
 * This is needed for two reasons:
 * - W3C recommendation is to quote custom font family names
 * - Some font family containing special characters will not work without quotes
 *   (easiest way to test - find a font name with a number in it)
 *
 * @example ```ts
 * toCSSString("Source Serif 4") // "'Source Serif 4'"
 * toCSSString(["Arial", "Helvetica", "sans-serif"]) // "'Arial', 'Helvetica', sans-serif"
 * ```
 */
export function toFontFamilyCSSString(value: string | string[]): string {
    const families = Array.isArray(value) ? value : [value];

    if (families.length > 1 && families.some((family) => globalValues.includes(family))) {
        throw new Error("Cannot mix global values with other values in font-family");
    }

    const result = families
        .map((family) => {
            if (reservedNames.includes(family) || globalValues.includes(family)) {
                return family;
            }

            // opting for single quotes since there are some libraries unsafely
            // manipulating DOM `style` attribute without escaping properly.
            return `'${family}'`;
        })
        .join(", ");

    return result;
}

// reserved names that must not be quoted
// should match the list defined in the CSS spec
// https://drafts.csswg.org/css-fonts/#generic-family-name-syntax
const reservedNames = [
    // generic script specific
    "generic(kai)",
    "generic(fangsong)",
    "generic(nastaliq)",

    // generic complete
    "serif",
    "sans-serif",
    "system-ui",
    "cursive",
    "fantasy",
    "math",
    "monospace",

    // generic incomplete
    "ui-serif",
    "ui-sans-serif",
    "ui-monospace",
    "ui-rounded",

    // not part of spec, but MDN defined them as generic
    "emoji",
    "fangsong",
];

const globalValues = ["inherit", "initial", "revert", "revert-layer", "unset"];
